import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { ChatMessage, ChatPart } from '@core/tellent-assistant-types';
import { IconButtonComponent } from '@core/user-interface-legacy';

import { TellentAssistantBrandMarkComponent } from '../brand-mark/tellent-assistant-brand-mark.component';
import { TellentAssistantInterruptPartComponent } from '../parts/interrupt-part/interrupt-part.component';
import { TellentAssistantReasoningPartComponent } from '../parts/reasoning-part/reasoning-part.component';
import { TellentAssistantTextPartComponent } from '../parts/text-part/text-part.component';
import { TellentAssistantToolCallPartComponent } from '../parts/tool-call-part/tool-call-part.component';
import { TellentAssistantToolResultPartComponent } from '../parts/tool-result-part/tool-result-part.component';

@Component({
  selector: 'tellent-assistant-message',
  standalone: true,
  imports: [
    IconButtonComponent,
    TellentAssistantBrandMarkComponent,
    TellentAssistantTextPartComponent,
    TellentAssistantReasoningPartComponent,
    TellentAssistantToolCallPartComponent,
    TellentAssistantToolResultPartComponent,
    TellentAssistantInterruptPartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <article [class]="'role-' + message().role">
      @if (message().role === 'assistant') {
        <tellent-assistant-brand-mark class="avatar" aria-hidden="true" />
      }
      <div class="body">
        @for (p of message().parts; track partKey(p)) {
          @switch (p.kind) {
            @case ('text') {
              <tellent-assistant-text-part [content]="p.content" [done]="p.done" />
            }
            @case ('reasoning') {
              <tellent-assistant-reasoning-part [content]="p.content" [done]="p.done" />
            }
            @case ('tool-call') {
              <tellent-assistant-tool-call-part
                [name]="p.name"
                [status]="p.status"
                [durationMs]="p.durationMs ?? null"
              />
            }
            @case ('tool-result') {
              <tellent-assistant-tool-result-part [part]="p" />
            }
            @case ('interrupt') {
              <tellent-assistant-interrupt-part
                [part]="p"
                (resolve)="onResolve(p.interruptId, $event)"
              />
            }
          }
        }
      </div>
      @if (message().role === 'assistant') {
        <rt-icon-button
          iconName="content-copy"
          size="small"
          variant="plain"
          class="copy"
          aria-label="Copy"
          (click)="copy()"
        />
      }
    </article>
  `,
  styleUrls: ['./tellent-assistant-message.component.less'],
})
export class TellentAssistantMessageComponent {
  public readonly message = input.required<ChatMessage>();
  public readonly resolve = output<{ id: string; payload: unknown }>();

  protected partKey(p: ChatPart): string {
    switch (p.kind) {
      case 'text':
      case 'reasoning':
        return `${p.kind}-${p.messageId}`;
      case 'tool-call':
      case 'tool-result':
        return `${p.kind}-${p.toolCallId}`;
      case 'interrupt':
        return `interrupt-${p.interruptId}`;
    }
  }

  protected onResolve(id: string, payload: unknown): void {
    this.resolve.emit({ id, payload });
  }

  protected async copy(): Promise<void> {
    const text = this.message()
      .parts.filter(p => p.kind === 'text')
      .map(p => (<Extract<ChatPart, { kind: 'text' }>>p).content)
      .join('\n');
    await navigator.clipboard.writeText(text);
  }
}
